import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  OnSelectionChangeParams,
  ReactFlowInstance,
} from '@xyflow/react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ConfirmDialog, TopBar } from '@aibuild/ui';
import MobileMenuButton from '@components/MobileMenuButton';
import useSnackbar from '@hooks/useSnackbar';
import { useProcessDetail } from './hooks/useProcessDetail';
import { useProcesses } from './hooks/useProcesses';
import { useProcessActions } from './hooks/useProcessActions';
import { useProcessPublish } from './hooks/useProcessPublish';
import { useProcessRename } from './hooks/useProcessRename';
import { useProcessSidePanelDialogs } from './hooks/useProcessSidePanelDialogs';
import { useDockCollapsedSync } from './hooks/useDockCollapsedSync';
import { resolveNodeDisplayState } from './editor/resolveNodeDisplayState';
import { GENERIC_NODE_FAILURE, UNNAMED_NODE_LABEL } from './editor/failureCopy';
import GraphCanvas from './editor/GraphCanvas';
import ProcessEditorTopBar from './editor/topbar/ProcessEditorTopBar';
import ProcessSidePanel from './editor/dock/ProcessSidePanel';
import { resolveRunnerLabel } from './editor/dock/resolveRunnerLabel';
import ProcessDetailsDialog from './editor/dock/ProcessDetailsDialog';
import ProcessRenameDialog from './editor/dock/ProcessRenameDialog';
import ProcessDuplicateDialog from './editor/dock/ProcessDuplicateDialog';
import ProcessDuplicateConfirmDialog from './editor/dock/ProcessDuplicateConfirmDialog';
import ProcessSendCopyDialog from './editor/dock/ProcessSendCopyDialog';
import { PUBLISHED_PROCESS_DELETE_NOTE } from './components/DeleteProcessDialog.constants';
import { useProcessGraph } from './editor/useProcessGraph';
import { useProcessClipboard } from './editor/useProcessClipboard';
import { reactFlowToDto } from './editor/graphTypes';
import { requiredAppsForSkills } from './editor/taskSkillSelection';
import { useProcessAutoSave } from './editor/useProcessAutoSave';
import { useProcessGraphSync } from './editor/useProcessGraphSync';
import { useStableCallback } from './editor/useStableCallback';
import { useNodeCollapse } from './editor/useNodeCollapse';
import { useWireStyle } from './editor/useWireStyle';
import { useCanvasOverlays } from './editor/useCanvasOverlays';
import { buildConnectionFocus } from './editor/connectionFocus';
import { autoArrange } from './editor/layout/autoArrange';
import {
  EditorContextProvider,
  type EditorContextValue,
  type NodeDisplayState,
} from './editor/EditorContext';
import type { ProcessEdge, ProcessNode } from './editor/graphTypes';
import CanvasToolbarTop from './editor/canvasToolbar/CanvasToolbarTop';
import CanvasToolbarBottom from './editor/canvasToolbar/CanvasToolbarBottom';
import NavLegend from './editor/canvasToolbar/NavLegend';
import RunHistorySheet from './editor/canvasToolbar/RunHistorySheet';
import ProcessStatusLegend, {
  type RunFailureItem,
} from './editor/validation/ProcessStatusLegend';
import { useClearProcessStatuses } from './hooks/useClearProcessStatuses';
import { useProcessRunsList } from './hooks/useProcessRunsList';
import { useMyLocalRunners, hasClaimableRunner } from './hooks/useMyLocalRunners';
import { useNodeDefinitions } from './hooks/useNodeDefinitions';
import { useRunHistoryStats } from './hooks/useRunHistoryStats';
import { useLastCompletedRunLog } from './hooks/useLastCompletedRunLog';
import { useSelectedNodeDock } from './editor/dock/useSelectedNodeDock';
import { useProcessRunSession } from './editor/useProcessRunSession';
import type {
  ActiveRun,
  NodeLiveState,
  ProcessRunSessionState,
} from './editor/runSession/types';
import { useManualCompletionPrompt } from './editor/useManualCompletionPrompt';
import { buildLiveExecutionSteps } from './editor/buildExecutionSteps';
import {
  getArtifactDownloadUrl,
  isTerminalNodeStatus,
  isTerminalRunStatus,
  type GateDecisionValue,
  type ProcessRunListItemDto,
  type RunInputValue,
} from '@api/processRuns';
import type { RunConfigFormValue } from './editor/run/RunConfigForm';
import type { ProcessNodeDto } from '@api/processes';
import TestRunDialog from './editor/TestRunDialog';
import RunDialog from './editor/RunDialog';
import GateDecisionDialog from './editor/GateDecisionDialog';
import RunnerClaimDialog from './editor/RunnerClaimDialog';
import ManualCompletionDialog from './editor/ManualCompletionDialog';
import InputValueDialog from './editor/InputValueDialog';
import DriftBlockerModal from './editor/banners/DriftBlockerModal';
import { useDriftHandlers } from './useDriftHandlers';
import RunDetailView from './run/RunDetailView';
import SseConnectionLostBanner from './editor/banners/SseConnectionLostBanner';
import { useGraphValidation } from './editor/useGraphValidation';
import {
  errorsByAffectedNodeId,
  groupByAffectedNodeId,
  type EdgeEndpoints,
  type ValidationError,
  type ValidationResult,
} from './editor/validation/types';
import {
  computeRunGate,
  type NodeRunGateState,
} from './editor/runStatus/upstreamGating';
import {
  CanvasArea,
  EmptyState,
  PageRoot,
} from './ProcessEditorPage.styled';
import { trackFunnelEvent } from '@utils/datadog';
import ProcessAssistantPanel from './editor/assistant/ProcessAssistantPanel';
import { useAssistantPanelState } from './editor/assistant/useAssistantPanelState';

const NO_ERRORS: readonly ValidationError[] = [];

/**
 * Coalesce the burst of `visibilitychange` events a fast alt-tab produces
 * into a single conditional GET.
 */
const VISIBILITY_SYNC_DEBOUNCE_MS = 250;

/**
 * Narrow React Flow's full edge shape down to the {@link EdgeEndpoints}
 * tuple {@link errorsByAffectedNodeId} needs. Extracted so the editor
 * page body stays inside Sonar's cognitive-complexity budget.
 */
function toEdgeEndpoints(
  edges: readonly { id: string; source: string; target: string }[],
): readonly EdgeEndpoints[] {
  return edges.map((e) => ({ id: e.id, source: e.source, target: e.target }));
}

/**
 * Identity-preserving id-list updater used by {@code onSelectionChange}:
 * returns {@code curr} when the new id sequence matches the previous one
 * so React's {@code Object.is} bail-out keeps the editor context
 * referentially stable across React Flow's per-drag-tick fan-outs.
 */
function stableIdList(
  curr: readonly string[],
  next: readonly { id: string }[],
): readonly string[] {
  if (curr.length !== next.length) return next.map((n) => n.id);
  for (let i = 0; i < curr.length; i += 1) {
    if (curr[i] !== next[i].id) return next.map((n) => n.id);
  }
  return curr;
}

/**
 * Resolve the tooltip displayed on the side-panel kebab's disabled Delete row.
 * Pulled out of the JSX so the component body stays a flat lookup instead of
 * a nested ternary, and so the copy lives in one named, testable place.
 *
 * Returns `undefined` when Delete is enabled (owner + draft) — the kebab row
 * doesn't render a tooltip in that case.
 */
const formatDeleteTooltip = (isOwner: boolean): string | undefined => {
  if (!isOwner) return 'Only the owner can delete this process';
  return undefined;
};

interface PageRootDataAttrs {
  readonly 'data-testid': 'process-editor';
  readonly 'data-selected-node-id': string;
  readonly 'data-error-count': number;
  readonly 'data-publish-blocker-count': number;
  readonly 'data-runnable': 'true' | 'false';
  readonly 'data-validation-checking': 'true' | 'false';
  readonly 'data-status': 'published' | 'draft';
  readonly 'data-dirty': 'true' | 'false';
}

/**
 * Build the `<PageRoot>` data attributes once, outside the render body, so
 * the four boolean→string ternaries don't pile onto the page component's
 * cognitive-complexity score. The attributes are read by tests as the
 * single stable hook into render state (selection, validation, dirty).
 */
const buildPageRootDataAttrs = ({
  selectedNodeId,
  errorCount,
  publishBlockerCount,
  validationValid,
  isChecking,
  isPublished,
  isDirty,
}: {
  selectedNodeId: string | null;
  errorCount: number;
  publishBlockerCount: number;
  validationValid: boolean;
  isChecking: boolean;
  isPublished: boolean;
  isDirty: boolean;
}): PageRootDataAttrs => ({
  'data-testid': 'process-editor',
  'data-selected-node-id': selectedNodeId ?? '',
  'data-error-count': errorCount,
  'data-publish-blocker-count': publishBlockerCount,
  'data-runnable': validationValid ? 'true' : 'false',
  'data-validation-checking': isChecking ? 'true' : 'false',
  'data-status': isPublished ? 'published' : 'draft',
  'data-dirty': isDirty ? 'true' : 'false',
});

const EditorGuardState: React.FC<{
  readonly testId: string;
  readonly children: React.ReactNode;
}> = ({ testId, children }) => (
  <PageRoot data-testid={testId}>
    <TopBar leading={<MobileMenuButton />} />
    <EmptyState>{children}</EmptyState>
  </PageRoot>
);

const renderEditorLoadGuard = (
  processId: string | undefined,
  detailQuery: {
    readonly isLoading: boolean;
    readonly isError: boolean;
    readonly data: unknown;
    readonly error: { readonly message?: string } | null;
  },
): React.ReactElement | null => {
  if (!processId) {
    return (
      <EditorGuardState testId="process-editor__missing-id">
        No process selected.
      </EditorGuardState>
    );
  }
  if (detailQuery.isLoading) {
    return (
      <EditorGuardState testId="process-editor__loading">
        Loading process…
      </EditorGuardState>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    const message = detailQuery.error?.message;
    return (
      <EditorGuardState testId="process-editor__error">
        Couldn&apos;t load this process.
        {message ? ` (${message})` : ''}
      </EditorGuardState>
    );
  }
  return null;
};

interface EditorOverlaysProps {
  readonly isRunActive: boolean;
  readonly runHistoryOpen: boolean;
  readonly isPublished: boolean;
  readonly processId: string;
  readonly processName: string;
  readonly onDuplicateProcess?: () => void;
  readonly onUnpublishProcess?: () => void;
  readonly onCloseRunHistory: () => void;
  /** Backend-validated graph state — drives the floating status legend. */
  readonly validation: ValidationResult;
  /**
   * Failed-node rows synthesised from the live/persisted per-node
   * states. The legend renders them alongside graph-validation issues
   * in the SAME list with the same click-to-focus affordance, and
   * escalates the tone to red when any are present.
   */
  readonly runFailures: readonly RunFailureItem[];
  readonly nodeCount: number;
  readonly connectionCount: number;
  /** True while the first /validate request is in flight (suppresses the legend). */
  readonly isValidationChecking: boolean;
  /** Click-to-locate from the legend's expanded issue list. */
  readonly onFocusNode: (nodeId: string) => void;
  readonly onFocusEdge: (edgeId: string) => void;
  readonly runs: readonly ProcessRunListItemDto[];
  readonly isRunsLoading: boolean;
}

/**
 * Floating canvas overlays — wraps the conditional top toolbar, the
 * always-mounted bottom toolbar, and the canvas-takeover run-history
 * sheet. Extracted into its own component so the conditional renders
 * (test-run banner takeover, sheet takeover) live in their own
 * cognitive-complexity scope, leaving the parent page free of those
 * branches in its render.
 */
const EditorOverlays: React.FC<EditorOverlaysProps> = ({
  isRunActive,
  runHistoryOpen,
  isPublished,
  processId,
  processName,
  onDuplicateProcess,
  onUnpublishProcess,
  onCloseRunHistory,
  validation,
  runFailures,
  nodeCount,
  connectionCount,
  isValidationChecking,
  onFocusNode,
  onFocusEdge,
  runs,
  isRunsLoading,
}) => {
  const showTopToolbar = !runHistoryOpen;
  // The legend shares the top-center space with the floating toolbar
  // and the run-history sheet, so it follows the same suppression
  // rules — surfacing it under the sheet would just clutter the
  // takeover surface.
  const showLegend = !isRunActive && !runHistoryOpen;
  return (
    <>
      {!runHistoryOpen && <NavLegend />}
      {showTopToolbar ? (
        <CanvasToolbarTop
          isPublished={isPublished}
          onDuplicateProcess={onDuplicateProcess}
          onUnpublishProcess={onUnpublishProcess}
        />
      ) : null}
      {showLegend ? (
        <ProcessStatusLegend
          validation={validation}
          runFailures={runFailures}
          nodeCount={nodeCount}
          connectionCount={connectionCount}
          isPublished={isPublished}
          isChecking={isValidationChecking}
          onFocusNode={onFocusNode}
          onFocusEdge={onFocusEdge}
        />
      ) : null}
      <CanvasToolbarBottom />
      {runHistoryOpen ? (
        <RunHistorySheet
          processId={processId}
          processName={processName}
          runs={runs}
          isLoading={isRunsLoading}
          onClose={onCloseRunHistory}
        />
      ) : null}
    </>
  );
};

/**
 * The full editor page (`/processes/:processId`). Composes:
 *
 *   ┌─ ProcessEditorTopBar (shared @aibuild/ui TopBar) ─────────────────────┐
 *   │ Processes › <name>  [Draft]      [chip] [actions] [theme] [profile]   │
 *   ├───────────────────────────────────────────────────────────────────────┤
 *   │ <DriftBlockerModal />   (only when auto-save returned 409 — blocks    │
 *   │                          the canvas behind a non-dismissible modal)   │
 *   ├───────────────────────────────────────────────────────────────────────┤
 *   │                                                                       │
 *   │                       GraphCanvas (React Flow)                        │
 *   │                                                                       │
 *   └───────────────────────────────────────────────────────────────────────┘
 *
 * The left sidebar (NodeLibrary) is rendered by the parent layout via
 * `ProcessEditorSidebar` so the swap with the global NavigationBar happens
 * at the layout boundary rather than inside the page.
 *
 * Validation surfaces ON the nodes themselves: each card picks up a red
 * border + the run-control morphs into a "Check" affordance with a
 * tooltip explaining the issue. The page delegates entirely to the
 * backend via {@link useGraphValidation} (debounced POST /validate)
 * and feeds the result down per-node via
 * {@link EditorContextValue.errorsForNode}.
 */

/**
 * Returns an identity-stable view of {@code value} — the reference
 * only changes when {@code isEqual} reports a substantive change.
 * Lets a downstream {@code useMemo} skip recomputation when the
 * upstream prop produced a new array reference for the same content.
 * Uses React's "set state during render" pattern so writes are safe
 * under concurrent rendering.
 */
function useStableValueRef<T>(value: T, isEqual: (prev: T, next: T) => boolean): T {
  const [stored, setStored] = useState<T>(value);
  if (value !== stored && !isEqual(stored, value)) {
    setStored(value);
    return value;
  }
  return stored;
}

/**
 * Structural node-array equality that IGNORES position so a drag tick
 * doesn't bust downstream memos. {@code applyNodeChanges} preserves
 * element references for unchanged nodes — the {@code a === b}
 * short-circuit makes this O(N) of cheap reference checks.
 */
function areNodesStructurallyEqual(
  prev: readonly ProcessNode[],
  next: readonly ProcessNode[],
): boolean {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i += 1) {
    const a = prev[i];
    const b = next[i];
    if (a === b) continue;
    if (a.id !== b.id || a.type !== b.type || a.data !== b.data) return false;
  }
  return true;
}

/**
 * Edge-array equivalent of {@link areNodesStructurallyEqual}.
 */
function areEdgesStructurallyEqual(
  prev: readonly ProcessEdge[],
  next: readonly ProcessEdge[],
): boolean {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i += 1) {
    const a = prev[i];
    const b = next[i];
    if (a === b) continue;
    if (
      a.id !== b.id ||
      a.source !== b.source ||
      a.target !== b.target ||
      a.sourceHandle !== b.sourceHandle ||
      a.targetHandle !== b.targetHandle ||
      a.type !== b.type ||
      a.data !== b.data
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Compute the failed-node rows for the topbar status legend.
 * Extracted from {@link ProcessEditorInner} so the parent stays under
 * SonarJS's cognitive-complexity budget — the union-narrowing + filter +
 * label fallback adds ~5 points if inlined.
 */
function useRunFailures(
  perNode: ProcessRunSessionState['perNode'],
  nodesById: Map<string, ProcessNode>,
): readonly RunFailureItem[] {
  return useMemo<readonly RunFailureItem[]>(() => {
    const out: RunFailureItem[] = [];
    for (const [nodeId, state] of perNode.entries()) {
      if (state.status !== 'failed') continue;
      const node = nodesById.get(nodeId);
      // ProcessNodeData is a discriminated union and one branch
      // (NoteNodeData) doesn't carry a `label`, so the union access
      // widens to `unknown` under the index-signature extension.
      // Narrow defensively rather than reach for `as` and skip the
      // type system.
      const rawLabel = (node?.data as { label?: unknown } | undefined)?.label;
      const label = typeof rawLabel === 'string' ? rawLabel.trim() : '';
      out.push({
        nodeId,
        nodeLabel: label.length > 0 ? label : UNNAMED_NODE_LABEL,
        message: state.errorMessage?.trim() || GENERIC_NODE_FAILURE,
      });
    }
    return out;
  }, [perNode, nodesById]);
}

type FlushBeforeStartResult =
  | { readonly ok: false }
  | { readonly ok: true; readonly expectedUpdatedAt: string | undefined };

interface AutoOpenRunDialogArgs {
  readonly searchParams: URLSearchParams;
  readonly setSearchParams: (
    nextInit: (prev: URLSearchParams) => URLSearchParams,
    opts?: { replace?: boolean },
  ) => void;
  readonly isPublished: boolean;
  readonly hasInputNodes: boolean;
  readonly onStartRun: (inputs: Record<string, RunConfigFormValue>) => Promise<void>;
  readonly setIsRunDialogOpen: (open: boolean) => void;
}

/**
 * Auto-open the run dialog when navigating with ?action=run (e.g. from
 * the process list page). Consumed once; the param is stripped so a
 * browser refresh doesn't re-open.
 *
 * <p>Extracted from {@link ProcessEditorInner} to keep its cognitive
 * complexity under SonarJS's budget — inline `if`+branching inside an
 * effect contributes disproportionately to the parent's score.</p>
 */
function useAutoOpenRunDialog({
  searchParams,
  setSearchParams,
  isPublished,
  hasInputNodes,
  onStartRun,
  setIsRunDialogOpen,
}: AutoOpenRunDialogArgs): void {
  // setSearchParams is async — until React flushes the URL update, this
  // effect re-renders still observe action=run. Without a latch the no-input
  // branch fires onStartRun on every re-render until the URL flushes,
  // which caused the runaway prepareRun cascade. Reset when action is
  // absent so a later navigation back here with ?action=run still fires.
  const consumedRef = useRef(false);
  useEffect(() => {
    if (searchParams.get('action') !== 'run') {
      consumedRef.current = false;
      return;
    }
    if (!isPublished) return;
    if (consumedRef.current) return;
    consumedRef.current = true;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('action');
      return next;
    }, { replace: true });
    if (hasInputNodes) {
      setIsRunDialogOpen(true);
    } else {
      onStartRun({}).catch(() => undefined);
    }
  }, [searchParams, isPublished, setSearchParams, hasInputNodes, onStartRun, setIsRunDialogOpen]);
}

interface AutoArrangeArgs {
  readonly searchParams: URLSearchParams;
  readonly setSearchParams: (
    nextInit: (prev: URLSearchParams) => URLSearchParams,
    opts?: { replace?: boolean },
  ) => void;
  readonly rfInstance: ReactFlowInstance<ProcessNode, ProcessEdge> | null;
  readonly nodes: readonly ProcessNode[];
  readonly edges: readonly ProcessEdge[];
  readonly applyLayout: (nodes: readonly ProcessNode[]) => void;
}

/**
 * Auto-arrange when navigating from template creation (?layout=auto).
 * Waits for the graph to load and the React Flow instance to be ready,
 * then runs dagre layout and fits the viewport. The param is stripped
 * so a refresh doesn't re-layout a graph the user may have rearranged.
 *
 * <p>Extracted from {@link ProcessEditorInner} for the same reason as
 * {@link useAutoOpenRunDialog} — the inline guard chain dominated the
 * parent's cognitive-complexity score.</p>
 */
function useAutoArrangeOnNavigation({
  searchParams,
  setSearchParams,
  rfInstance,
  nodes,
  edges,
  applyLayout,
}: AutoArrangeArgs): void {
  const layoutConsumedRef = useRef(false);
  useEffect(() => {
    if (layoutConsumedRef.current) return;
    if (searchParams.get('layout') !== 'auto') return;
    if (!rfInstance || nodes.length === 0) return;
    layoutConsumedRef.current = true;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('layout');
      return next;
    }, { replace: true });
    const laidOut = autoArrange(nodes, edges);
    applyLayout(laidOut);
    requestAnimationFrame(() => {
      rfInstance.fitView({ padding: 0.2, duration: 250 });
    });
  }, [searchParams, setSearchParams, rfInstance, nodes, edges, applyLayout]);
}

/**
 * "A run that will (re)compute this node is in flight." Driven off the
 * session — NOT the per-node resolved status — so it stays true through
 * the window right after a single-node run starts, where the live map has
 * been cleared but the SSE seed / first node_state hasn't landed yet (the
 * resolved status would briefly report the stale persisted state there).
 * Extracted to module scope so the branches don't inflate the editor
 * component's cognitive complexity. See AIB-7181.
 */
function computeNodeGenerating(
  nodeId: string,
  live: NodeLiveState | undefined,
  active: ActiveRun | null,
  startInFlight: boolean,
): boolean {
  if (live && !isTerminalNodeStatus(live.status)) return true;
  if (!active) return startInFlight;
  if (isTerminalRunStatus(active.runStatus)) return false;
  // Node run: only the targeted node. Full run: the per-node live status
  // above already covers nodes the engine is actually working.
  return active.mode.kind === 'node' && active.mode.nodeId === nodeId;
}

const ProcessEditorInner: React.FC = () => {
  const { processId } = useParams<{ processId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useSnackbar();
  const detailQuery = useProcessDetail(processId);
  const processActions = useProcessActions();
  // Slice nodes + edges off the detail payload so `useProcessGraph` keeps
  // its narrow `ProcessGraphDto` contract (no process metadata leakage into
  // the editor's local state).
  const initialGraph = useMemo(
    () =>
      detailQuery.data
        ? { nodes: detailQuery.data.nodes, edges: detailQuery.data.edges }
        : undefined,
    [detailQuery.data],
  );
  const graph = useProcessGraph(initialGraph, processId);
  // The id of the currently-selected node, captured from React Flow's
  // selection events. The inspector (PR3) keys off this; for now the value
  // is also stamped onto the page root (data attribute) so tests have a
  // stable hook without re-implementing selection tracking.
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  // Full multi-selection -- powers the canvas-toolbar Edit group
  // (Duplicate / Delete on every selected node, not just the first).
  // Captured from the same `onSelectionChange` event so the two stay in
  // lockstep without an extra subscription.
  const [selectedNodeIds, setSelectedNodeIds] = useState<readonly string[]>([]);
  // Edge selection feeds the toolbar's Delete button so users can drop
  // a wire from the bin icon (AIB-7081) — the Del shortcut already
  // worked via React Flow's default keybinding, but the toolbar
  // ignored bare-edge selections.
  const [selectedEdgeIds, setSelectedEdgeIds] = useState<readonly string[]>([]);
  // Persisted UI-only flags consumed by the floating canvas toolbars.
  const nodeCollapse = useNodeCollapse();
  const wireStylePref = useWireStyle();
  const overlays = useCanvasOverlays();
  // Capture the React Flow instance so the toolbars can drive zoom /
  // fit / centre via the editor context. The instance is stable
  // across renders once set, so consumers depending on it via
  // useCallback don't tear unnecessarily.
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance<
    ProcessNode,
    ProcessEdge
  > | null>(null);
  const onCanvasInit = useCallback(
    (instance: ReactFlowInstance<ProcessNode, ProcessEdge>) => {
      setRfInstance(instance);
    },
    [],
  );
  // Run-history sheet open/close (canvas takeover for published).
  const [runHistoryOpen, setRunHistoryOpen] = useState(false);

  // Reset all process-scoped UI state whenever the route's processId
  // changes. Without this the previous process's selection (drives
  // toolbar Duplicate / Delete enablement) and run-history open state
  // would leak into the freshly-loaded process, leading to confusing
  // toolbar affordances and a sheet pinned over an unrelated graph.
  // The graph itself is reset by useProcessGraph's processId sentinel;
  // this effect handles the page-level state hooks that live above it.
  useEffect(() => {
    setSelectedNodeId(null);
    setSelectedNodeIds([]);
    setRunHistoryOpen(false);
  }, [processId]);
  // POST .../clear-statuses mutation -- wipes every node's draft run
  // status. The toolbar exposes it as "Clear all" when at least one
  // node has a non-idle run state.
  const clearStatusesMutation = useClearProcessStatuses();

  const isPublished = detailQuery.data?.status === 'published';
  const ownerName = detailQuery.data?.createdBy?.name ?? '';

  const allProcessesQuery = useProcesses({ sort: 'newest', size: 50 });
  const processSummaries = useMemo(() => {
    if (!allProcessesQuery.data) return [];
    const seen = new Set<string>();
    return allProcessesQuery.data.pages
      .flatMap((page) => page.processes.map((p) => ({ id: p.processId, name: p.name })))
      .filter(({ id }) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }, [allProcessesQuery.data]);

  // Stabilise the (nodes, edges) tuple against position-only changes so
  // a drag tick (which mutates `position` but leaves `data` reference
  // intact) doesn't cascade through the validation hook → errorsByNode →
  // editorContext → every {@link ProcessNode} re-render. Validation only
  // cares about structure (labels, ports, config, edges) — never
  // position — so the structural-equality comparator is sound.
  const stableNodes = useStableValueRef(graph.nodes, areNodesStructurallyEqual);
  const stableEdges = useStableValueRef(graph.edges, areEdgesStructurallyEqual);
  const connectionFocus = useMemo(
    () => buildConnectionFocus(selectedNodeIds, stableEdges),
    [selectedNodeIds, stableEdges],
  );

  const hasInputNodes = useMemo(
    () =>
      stableNodes.some(
        (n) =>
          n.type === 'input' &&
          (typeof (n.data as { dataType?: unknown })?.dataType !== 'string' ||
            (n.data as { dataType?: string }).dataType !== 'NONE'),
      ),
    [stableNodes],
  );

  // Wire-DTO snapshot fed to both auto-save (full positions) and the
  // backend validate hook (structural snapshot — no re-fire on drag).
  const writeGraph = useMemo(
    () => reactFlowToDto(graph.nodes, graph.edges),
    [graph.nodes, graph.edges],
  );
  const validationGraph = useMemo(
    () => reactFlowToDto(stableNodes, stableEdges),
    [stableNodes, stableEdges],
  );

  // Backend is the sole validator: a stateless POST /validate runs on a
  // tight (200ms) debounce after every structural edit. The hook seeds
  // from the detail's embedded validation so first paint is instant.
  const { validation, isValidating } = useGraphValidation({
    processId,
    writeGraph: validationGraph,
    seedFromDetail: detailQuery.data?.validation ?? null,
  });
  const errorCount = validation.errors.length;
  const publishBlockerCount = validation.publishBlockers.length;
  const isChecking = isValidating || (!detailQuery.data && detailQuery.isLoading);
  // Per-node error index — looked up by the EditorContext consumer so the
  // node renderer (red border) and run-control (Check affordance +
  // tooltip) both read from the same validation result without
  // re-scanning the error list themselves. Edge-anchored errors (e.g.
  // PORT_TYPE_MISMATCH from a 3D-model output into an image-gen input)
  // are attributed to the edge's TARGET node so the per-node Run button
  // refuses to run a node whose incoming wire is broken — without this
  // the single-node run path bypassed the validation that the
  // whole-process Run respects.
  const errorsByNode = useMemo(
    () => errorsByAffectedNodeId(validation, toEdgeEndpoints(stableEdges)),
    [validation, stableEdges],
  );
  const errorsForNode = useCallback(
    (nodeId: string): readonly ValidationError[] =>
      errorsByNode[nodeId] ?? NO_ERRORS,
    [errorsByNode],
  );
  // Publish blockers travel on their own channel: they paint the amber
  // "wire me up" ring but must not disable the node's Run button, which
  // reads errorsForNode.
  const publishHintsByNode = useMemo(
    () =>
      groupByAffectedNodeId(
        validation.publishBlockers,
        toEdgeEndpoints(stableEdges),
      ),
    [validation.publishBlockers, stableEdges],
  );
  const publishHintsForNode = useCallback(
    (nodeId: string): readonly ValidationError[] =>
      publishHintsByNode[nodeId] ?? NO_ERRORS,
    [publishHintsByNode],
  );

  const graphNodesRef = useRef(graph.nodes);
  const graphEdgesRef = useRef(graph.edges);
  useEffect(() => {
    graphNodesRef.current = graph.nodes;
    graphEdgesRef.current = graph.edges;
  }, [graph.nodes, graph.edges]);

  const unchosenBranchNodeIds = useCallback(
    (gateNodeId: string, decision: string): readonly string[] => {
      const nodes = graphNodesRef.current;
      const edges = graphEdgesRef.current;
      const gateNode = nodes.find((n) => n.id === gateNodeId);
      if (!gateNode) return [];
      const ports = (gateNode.data as { ports?: readonly { portId: string; direction: string; label: string }[] }).ports;
      if (!ports) return [];
      const chosenLabel = decision.toLowerCase();
      const unchosenPortIds = ports
        .filter(
          (p) =>
            p.direction === 'output' &&
            p.label.toLowerCase() !== chosenLabel,
        )
        .map((p) => p.portId);
      if (unchosenPortIds.length === 0) return [];
      const portSet = new Set(unchosenPortIds);
      return edges
        .filter((e) => e.source === gateNodeId && portSet.has(e.sourceHandle ?? ''))
        .map((e) => e.target);
    },
    [],
  );

  // Assigned below: graph-sync depends on state this hook is declared ahead of.
  const syncGraphFromServerRef = useRef<() => void>(() => undefined);
  const onStaleGraph = useCallback(() => syncGraphFromServerRef.current(), []);
  const session = useProcessRunSession({
    processId,
    hasActiveTestRun: detailQuery.data?.hasActiveTestRun ?? false,
    isPublished: detailQuery.data?.status === 'published',
    unchosenBranchNodeIds,
    onStaleGraph,
  });
  // ── Run history + execution log data ────────────────────────────────────
  const { runs, isLoading: isRunsLoading } = useProcessRunsList(processId);
  const runHistoryStats = useRunHistoryStats(runs, Date.now());
  const lastRunLog = useLastCompletedRunLog(processId, session.lastTerminalRun);

  // ── Selected-node data for the dock ───────────────────────────────────
  const lastRunId = session.active?.mode.runId
    ?? session.lastTerminalRun?.runId
    ?? (runs.length > 0 ? runs[0].runId : null);
  const selectedNode = useSelectedNodeDock({
    processId,
    selectedNodeId,
    nodes: stableNodes,
    runId: lastRunId,
    liveStateForNode: session.liveStateForNode,
  });

  const liveExecData = useMemo(
    () =>
      buildLiveExecutionSteps(
        session.active,
        session.perNode,
        stableNodes,
        stableEdges,
      ),
    [session.active, session.perNode, stableNodes, stableEdges],
  );

  // Active test runs freeze the editor: auto-save off, canvas read-only,
  // banner up. The user can still browse the graph but not mutate it
  // until the run terminates (or they cancel).
  const isRunActive = session.active != null;
  // Single-node test runs also count as "a run is in progress" for the
  // per-node gate: only one node can be running at once. We surface this
  // as a separate flag so the gate can pick the right disable reason
  // (`run_in_progress` vs `read_only`).
  const isSingleNodeRunning = session.active?.mode.kind === 'node';

  // `!isRunActive` only flips true after the run-session hook
  // resolves; the detail payload's server-side flag closes the
  // mount-time gap that otherwise 409s as TEST_RUN_ACTIVE.
  const autoSaveEnabled =
    Boolean(detailQuery.data) &&
    !isPublished &&
    !isRunActive &&
    !detailQuery.data?.hasActiveTestRun;
  const autoSave = useProcessAutoSave({
    processId,
    nodes: writeGraph.nodes,
    edges: writeGraph.edges,
    detail: detailQuery.data,
    enabled: autoSaveEnabled,
  });

  // Force any in-flight / debounced auto-save to land BEFORE we start a
  // run. Without this, clicking Run within the auto-save debounce
  // window (default 1500 ms) -- or right after a textarea blur that
  // only just committed to local graph state -- launches the backend
  // run against the previously-persisted graph, so the user's most
  // recent edit (e.g. a new output port instruction) silently doesn't
  // take effect until the *next* Run.
  //
  // Returns `ok` when the run may proceed, and refuses otherwise:
  //   - drift state -> we refuse and prompt to refresh, because the
  //     backend would reject the PUT anyway and we'd otherwise be
  //     running an obsolete version of the graph.
  //   - flush() reject -> autoSave's own state machine already shows
  //     the failure in the SaveStatusIndicator; we add a snackbar so
  //     the user understands why the Run didn't start.
  // Published processes and active runs disable auto-save entirely
  // (`enabled=false` above) -- in that mode flush() is a cheap no-op
  // so this helper still returns ok=true and the run proceeds.
  // The assistant reads the graph from the server, so its turn needs the
  // same guarantee for the same reason.
  //
  // `expectedUpdatedAt` comes from the flush response as in
  // {@link useProcessPublish}, never from autoSave.lastSavedAt: that
  // baseline goes stale after clear-statuses and would manufacture drift.
  const flushBeforeStart = useCallback(async (): Promise<FlushBeforeStartResult> => {
    if (autoSave.status === 'drift') {
      showError('Remote changes detected. Refresh to load them first.');
      return { ok: false };
    }
    try {
      const flushed = await autoSave.flush();
      return {
        ok: true,
        expectedUpdatedAt: flushed?.updatedAt ?? detailQuery.data?.updatedAt,
      };
    } catch {
      showError('Could not save your latest changes. Try again.');
      return { ok: false };
    }
  }, [autoSave, detailQuery.data?.updatedAt, showError]);

  const flushBeforeAssistantTurn = useCallback(
    async (): Promise<boolean> => (await flushBeforeStart()).ok,
    [flushBeforeStart],
  );

  // Drift escape hatches — both surfaced by the {@link DriftBlockerModal}
  // (and `onRefreshAfterDrift` also by the SaveStatusIndicator pill
  // when the modal isn't visible because, e.g., a sibling open dialog
  // would z-order under our backdrop). Bundled in a sub-hook so the
  // page body doesn't carry their useCallback bookkeeping.
  //
  // {@code onRefreshAfterDrift}: invalidate the detail query so the
  // editor reloads server state. The autosave hook's drift-recovery
  // effect re-baselines {@code expectedUpdatedAt} off the fresh payload.
  //
  // {@code onForceSaveOverDrift}: last-writer-wins -- the hook
  // re-stamps {@code expectedUpdatedAt} from the original 409 and
  // retries the PUT with the user's local canvas. We swallow the
  // rejection because the state machine already drives every visible
  // surface (modal stays mounted on a 2nd drift; status pill flips to
  // 'error' on a 5xx / validation failure).
  const {
    onRefresh: onRefreshAfterDrift,
    onForceSave: onForceSaveOverDrift,
  } = useDriftHandlers(
    processId,
    queryClient,
    autoSave.forceSave,
    graph.setGraph,
    autoSave.confirmDriftRecovery,
    autoSave.cancelDriftRecovery,
  );
  // Show the blocking modal whenever auto-save is in `drift`. There's
  // no dismiss path -- the user must commit to either Refresh or
  // Save-anyway. The hook also tracks an `isForceSaving` boolean which
  // we pass through so the modal can show a spinner without flipping
  // status off `drift` (keeping the modal mounted if a second 409
  // races our force-save).
  const showDriftBlocker = autoSave.status === 'drift';

  // Reported by the assistant panel, from the moment a message is sent
  // until its turn ends.
  const [assistantBusy, setAssistantBusy] = useState(false);

  // Effective read-only: the editor freezes for published processes
  // (immutable contract), while a test run is in flight (we don't want
  // the user mutating the graph mid-run because the SSE-driven
  // `latestExecution` snapshot has to point at the same nodes the
  // engine just executed against), AND while we're showing the drift
  // blocker modal. The drift freeze is the silent-edit-loss fix from
  // AIB-6976: without it the user could continue editing behind the
  // modal's backdrop and have those edits silently dropped when they
  // chose Refresh.
  // A turn in flight freezes the canvas too: the assistant is about to
  // rewrite this graph, and an edit landing in that window is a genuine
  // conflict with no good resolution — the user would be editing a version
  // the assistant has already superseded.
  const effectiveReadOnly =
    isPublished || isRunActive || showDriftBlocker || assistantBusy;
  // Node dragging is a strictly weaker affordance than full editing:
  // on a Published process we let the user reposition nodes for
  // readability, but the moves are ephemeral (auto-save is gated
  // separately on `!isPublished` so positions never persist; a
  // refresh restores the published layout). During an active run we
  // still pin everything so the live overlay matches the served
  // layout the engine is referencing. An assistant turn pins them for
  // the same reason as the rest of the freeze: this drag would persist,
  // onto a graph the assistant is in the middle of replacing.
  const canDragNodes = !isRunActive && !assistantBusy;

  // Lazy getter for "current cursor position in canvas world coords".
  // Set by GraphCanvas via `onCursorWorldPosRef` once it has a React
  // Flow instance. We hold it in a ref instead of state so a paste
  // shortcut can read the freshest value without forcing a re-render
  // on every mousemove.
  const cursorWorldPosGetterRef = useRef<
    (() => { x: number; y: number } | null) | null
  >(null);
  const onCursorWorldPosRef = useCallback(
    (getter: () => { x: number; y: number } | null) => {
      cursorWorldPosGetterRef.current = getter;
    },
    [],
  );

  const clipboard = useProcessClipboard({
    selectedNodeIds,
    nodes: graph.nodes,
    pasteNodes: graph.pasteNodes,
    disabled: effectiveReadOnly,
    getCursorWorldPos: useCallback(
      () => cursorWorldPosGetterRef.current?.() ?? null,
      [],
    ),
  });

  // Cmd/Ctrl + C / V on the editor canvas. Lives at the page level so
  // a single listener serves the whole editor surface. Bails when:
  //   - The editor is read-only (Published process or active run).
  //   - Focus is inside a text-entry control (input / textarea /
  //     contenteditable) or an open modal — preserves OS-level
  //     copy / paste in node config dialogs and the gate decision
  //     modal.
  // We deliberately do NOT touch the OS clipboard (`navigator.clipboard`)
  // — see {@code useProcessClipboard} for the rationale.
  useEffect(() => {
    if (effectiveReadOnly) return undefined;
    const handler = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key !== 'c' && event.key !== 'v') return;
      // event.target can be the Document when the event was
      // dispatched directly at it (jsdom path; happens in tests too).
      // Only Element-typed targets have `closest()`, so we narrow
      // before consulting it.
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          'input, textarea, [contenteditable="true"], [role="dialog"]',
        )
      ) {
        return;
      }
      if (event.key === 'c') {
        // Don't preventDefault — if the user has a text selection in
        // some non-input element, OS-level copy should still work.
        clipboard.copySelected();
        return;
      }
      // Paste: explicitly preventDefault so a stray paste targeted at
      // a focused button element doesn't sneak through and crash the
      // browser's default paste path.
      event.preventDefault();
      clipboard.paste();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [clipboard, effectiveReadOnly]);

  // Keyed off {@code stableNodes} so a drag tick (position-only)
  // doesn't bust the Map and cascade into a context rebuild.
  const nodesById = useMemo(() => {
    const m = new Map<string, ProcessNode>();
    stableNodes.forEach((n) => m.set(n.id, n));
    return m;
  }, [stableNodes]);

  // Failed-node rows for the topbar {@link ProcessStatusLegend}. We
  // surface any node whose latest run-state row is `failed` — both the
  // live in-flight run and the persisted cold-load seed end up in
  // `session.perNode`, so a refresh after a failed run keeps the rows
  // alive (same fix as the dock's error banner). The legend renders
  // these alongside graph-validation issues using the same row UI;
  // clicking a row pans the canvas to the failed node via
  // {@link onFocusNode}.
  const runFailures = useRunFailures(session.perNode, nodesById);

  const refreshArtifactUrl = useCallback(
    async (artifactId: string): Promise<string> => {
      if (!processId) return '';
      const fresh = await getArtifactDownloadUrl(processId, artifactId);
      return fresh.downloadUrl;
    },
    [processId],
  );

  // INPUT node-run prompt: when the user clicks "Run from here" on an
  // INPUT node, we collect a value via {@link InputValueDialog} before
  // dispatching. For every other node type, single-node runs go through
  // immediately and the engine resolves upstream artifacts as before.
  // We stash the live React-Flow node so the dialog can render the right
  // upload / library / text affordances for the node's `dataType` and so
  // the title always reflects the user-defined label.
  const [pendingInputNodeId, setPendingInputNodeId] = useState<string | null>(
    null,
  );
  const pendingInputNode = pendingInputNodeId
    ? (nodesById.get(pendingInputNodeId) ?? null)
    : null;

  const startNodeRunFromContext = useCallback(
    (nodeId: string) => {
      const node = nodesById.get(nodeId);
      if (node?.type === 'input') {
        // Read dataType defensively: a partially-hydrated node (e.g.
        // during definition refetch) may not yet carry a string here.
        // Anything other than the literal 'NONE' falls back to the
        // interactive InputValueDialog path so we don't accidentally
        // launch a run without prompting for the missing value.
        const data: { dataType?: unknown } | undefined = node.data as
          | { dataType?: unknown }
          | undefined;
        const dataType = data?.dataType;
        if (typeof dataType === 'string' && dataType === 'NONE') {
          void (async () => {
            try {
              if (!(await flushBeforeStart()).ok) return;
              await session.startNodeRun(nodeId);
            } catch {
              // session.startNodeRun has already surfaced the
              // user-facing snackbar via its own error handler; we
              // catch here only to keep the fire-and-forget IIFE from
              // raising an unhandled rejection (which Sentry / the
              // browser console would log as a noisy "unhandled
              // promise" event with no actionable context).
            }
          })();
          return;
        }
        setPendingInputNodeId(nodeId);
        return;
      }
      void (async () => {
        try {
          if (!(await flushBeforeStart()).ok) return;
          await session.startNodeRun(nodeId);
        } catch {
          // See note above -- snackbar already shown by the session.
        }
      })();
    },
    [nodesById, flushBeforeStart, session],
  );

  const onCloseInputDialog = useCallback(() => setPendingInputNodeId(null), []);
  const onSubmitInputValue = useCallback(
    async (value: RunInputValue) => {
      if (!pendingInputNodeId) return;
      if (!(await flushBeforeStart()).ok) return;
      const nodeId = pendingInputNodeId;
      try {
        await session.startNodeRun(nodeId, { inputValue: value });
        // Only close the dialog after the start request has been
        // accepted. If we cleared pendingInputNodeId before the await,
        // a server-side rejection (e.g. UPLOAD_NOT_FOUND, validation)
        // would tear the dialog down before the user sees why -- the
        // snackbar fires but the form state (selected upload, typed
        // text) is gone and they have to redo everything.
        setPendingInputNodeId(null);
      } catch {
        // Snackbar / inputError already populated by startNodeRun;
        // leaving the dialog mounted lets the user retry without
        // re-entering their value.
      }
    },
    [pendingInputNodeId, flushBeforeStart, session],
  );

  // the React Flow node data. The session's perNode map is seeded on
  // reconnect from {@link getRun} and refreshed by the {@code reconnected}
  // SSE envelope, so any node currently touched by the active run wins via
  // the live branch -- no extra full-run staleness guard needed. The
  // actual lookup lives in {@link resolveNodeDisplayState} so the page's
  // cognitive complexity stays bounded.
  const runStateForNode = useCallback(
    (nodeId: string): NodeDisplayState =>
      resolveNodeDisplayState(nodeId, session.liveStateForNode, nodesById),
    [nodesById, session],
  );

  // Per-node Run gate: combines the global "is the editor allowed to
  // start a run right now?" state (read-only / another run already in
  // flight) with the per-node {@link computeRunGate} graph analysis. The
  // gate is consumed by the per-node header Run button (via `useNodeRun(id)`).
  const detailNodeViews = useMemo(() => {
    const nodes = detailQuery.data?.nodes ?? [];
    return nodes.map((n: ProcessNodeDto) => ({
      nodeId: n.nodeId,
      kind: n.kind,
      label: n.label,
      // Note is the only non-computable kind today; mirrors the backend's
      // {@code NodeDefinition.isComputable()} default. The catalog hook
      // could refine this per-definition, but the gate logic only uses it
      // to short-circuit non-runnable nodes - and only Note matches.
      isRunnable: n.kind !== 'note',
      latestExecution: n.latestExecution,
      ports: n.ports,
    }));
  }, [detailQuery.data?.nodes]);

  const detailEdges = useMemo(
    () => detailQuery.data?.edges ?? [],
    [detailQuery.data?.edges],
  );

  const getRunGate = useCallback(
    (nodeId: string): NodeRunGateState => {
      if (effectiveReadOnly) {
        return { canRun: false, reason: 'read_only' };
      }
      if (isSingleNodeRunning) {
        // Running this node is fine if it IS the running one — but the
        // session orchestrator enforces single-run-at-a-time, so any
        // start request here is a no-op. Treat as locked.
        return { canRun: false, reason: 'run_in_progress' };
      }
      return computeRunGate(nodeId, detailNodeViews, detailEdges);
    },
    [effectiveReadOnly, isSingleNodeRunning, detailNodeViews, detailEdges],
  );

  // ── Canvas toolbar action plumbing ─────────────────────────────────────
  // Click-handlers read the latest graph via refs so their identity
  // stays stable across drag ticks (closing over {@code graph.nodes}
  // directly would churn identity per tick and invalidate the editor
  // context for every consumer).
  const liveNodesRef = useRef<readonly ProcessNode[]>(graph.nodes);
  const liveEdgesRef = useRef<readonly ProcessEdge[]>(graph.edges);
  useEffect(() => {
    liveNodesRef.current = graph.nodes;
    liveEdgesRef.current = graph.edges;
  }, [graph.nodes, graph.edges]);
  const { applyLayout, removeNodes, removeEdges, duplicateNodes } = graph;
  const onAutoArrange = useCallback(() => {
    const laidOut = autoArrange(liveNodesRef.current, liveEdgesRef.current);
    applyLayout(laidOut);
    // Re-fit the viewport after a layout change so the user sees the
    // whole rearranged graph instead of being left with whatever
    // viewport was framing the pre-layout positions.
    requestAnimationFrame(() => {
      rfInstance?.fitView({ padding: 0.2, duration: 250 });
    });
  }, [applyLayout, rfInstance]);

  const onSetAllNodesCollapsed = useCallback(
    (collapsed: boolean) => {
      nodeCollapse.setAllCollapsed(
        stableNodes.map((n) => n.id),
        collapsed,
      );
    },
    [stableNodes, nodeCollapse],
  );

  const onZoomIn = useCallback(() => {
    rfInstance?.zoomIn({ duration: 150 });
  }, [rfInstance]);
  const onZoomOut = useCallback(() => {
    rfInstance?.zoomOut({ duration: 150 });
  }, [rfInstance]);
  const onFitView = useCallback(() => {
    rfInstance?.fitView({ padding: 0.2, duration: 200 });
  }, [rfInstance]);
  const onRecenter = useCallback(() => {
    if (!rfInstance) return;
    const live = liveNodesRef.current;
    if (live.length === 0) {
      rfInstance.setCenter(0, 0, { duration: 200, zoom: rfInstance.getZoom() });
      return;
    }
    // Compute the centroid of the nodes' top-left positions so the
    // viewport pans to the visual centre of the graph without
    // changing the user's current zoom level.
    const sum = live.reduce(
      (acc, n) => ({ x: acc.x + n.position.x, y: acc.y + n.position.y }),
      { x: 0, y: 0 },
    );
    const cx = sum.x / live.length;
    const cy = sum.y / live.length;
    rfInstance.setCenter(cx, cy, {
      duration: 200,
      zoom: rfInstance.getZoom(),
    });
  }, [rfInstance]);

  // Click-to-locate from {@link ProcessStatusLegend}. Pans the viewport
  // onto the offending element and flips its selection on so the
  // per-node validation surface (red border + Check affordance) wins
  // the user's attention. We don't change zoom — the user's chosen
  // working zoom is preserved, only the centre moves.
  const onFocusNode = useCallback(
    (nodeId: string) => {
      if (!liveNodesRef.current.some((n) => n.id === nodeId)) return;
      graph.selectNode(nodeId);
      if (rfInstance) {
        rfInstance.fitView({
          nodes: [{ id: nodeId }],
          padding: 0.4,
          duration: 280,
        });
      }
    },
    [graph, rfInstance],
  );
  const onFocusEdge = useCallback(
    (edgeId: string) => {
      const target = liveEdgesRef.current.find((e) => e.id === edgeId);
      if (!target) return;
      graph.selectEdge(edgeId);
      // React Flow's fitView accepts node ids only, so for edge focus
      // we centre on the source node — close enough that the edge
      // (and its target) are both visible after the pan.
      if (rfInstance) {
        rfInstance.fitView({
          nodes: [{ id: target.source }, { id: target.target }],
          padding: 0.4,
          duration: 280,
        });
      }
    },
    [graph, rfInstance],
  );

  // Clear-all visibility: the toolbar shows "Clear" only when at least
  // one node has a non-idle run state (live SSE-driven OR persisted
  // latestExecution). We compute the bool here so the context exposes
  // exactly what the toolbar needs. Keyed off `stableNodes` so dragging
  // doesn't re-iterate every node on every mouse-move.
  const hasNodeRunStates = useMemo(
    () => stableNodes.some((n) => runStateForNode(n.id).status !== 'idle'),
    [stableNodes, runStateForNode],
  );

  // Run-level runner label for the execution log header, derived from the
  // graph's declared Task-node targets (cloud / local / mixed).
  const runnerLabel = useMemo(() => resolveRunnerLabel(stableNodes), [stableNodes]);

  // Drives the generation nodes' overlay + forward-only preview hold.
  // Logic in {@link computeNodeGenerating} (module scope).
  const sessionActive = session.active;
  const sessionStartInFlight = session.startInFlight;
  const sessionLiveStateForNode = session.liveStateForNode;
  const isNodeGenerating = useCallback(
    (nodeId: string): boolean =>
      computeNodeGenerating(
        nodeId,
        sessionLiveStateForNode(nodeId),
        sessionActive,
        sessionStartInFlight,
      ),
    [sessionActive, sessionStartInFlight, sessionLiveStateForNode],
  );
  // Run-level counterpart to isNodeGenerating: true from the moment Run is
  // clicked until the run terminates, whichever node the engine is on.
  // Distinct from the editor-freeze `isRunActive` above, which stays true for
  // a terminated-but-still-selected run.
  const runInFlight =
    sessionStartInFlight ||
    (sessionActive !== null && !isTerminalRunStatus(sessionActive.runStatus));
  const onClearStatuses = useCallback(() => {
    if (!processId) return;
    clearStatusesMutation.mutate(processId, {
      onSuccess: () => {
        session.resetLiveStates();
        showSuccess('Cleared all node run statuses.');
      },
      onError: (err) => showError(err.message),
    });
  }, [clearStatusesMutation, processId, session, showError, showSuccess]);

  const onRemoveSelectedNodes = useCallback(() => {
    if (selectedNodeIds.length === 0 && selectedEdgeIds.length === 0) return;
    if (selectedNodeIds.length > 0) removeNodes(selectedNodeIds);
    if (selectedEdgeIds.length > 0) removeEdges(selectedEdgeIds);
    setSelectedNodeIds([]);
    setSelectedEdgeIds([]);
    setSelectedNodeId(null);
  }, [removeNodes, removeEdges, selectedNodeIds, selectedEdgeIds]);

  const onDuplicateSelectedNodes = useCallback(() => {
    if (selectedNodeIds.length === 0) return;
    duplicateNodes(selectedNodeIds);
  }, [duplicateNodes, selectedNodeIds]);

  const onDuplicateNode = useCallback(
    (id: string) => {
      duplicateNodes([id]);
    },
    [duplicateNodes],
  );

  // Event-handler callbacks are trampolined so their identity never
  // changes (drops them from the editorContext deps below). Render-time
  // derivations — {@code errorsForNode}, {@code runStateForNode},
  // {@code getRunGate}, {@code isNodeCollapsed} — are intentionally
  // NOT trampolined: memoised consumers like {@link ProcessNode} need
  // the context to invalidate when their underlying data flips,
  // otherwise validation / run-state UI would stay stale until a prop
  // actually changes.
  const stableUpdateNodeData = useStableCallback(graph.updateNodeData);
  const stableRemoveNode = useStableCallback(graph.removeNode);
  const stableDuplicateNode = useStableCallback(onDuplicateNode);
  const stableStartNodeRun = useStableCallback(startNodeRunFromContext);
  const stableCancelNodeRun = useStableCallback(() => {
    void session.cancelActive();
  });
  const stableRefreshArtifactUrl = useStableCallback(refreshArtifactUrl);
  const stableSetRunHistoryOpen = useStableCallback(setRunHistoryOpen);
  const stableToggleNodeCollapsed = useStableCallback(nodeCollapse.toggleCollapsed);
  const stableSetAllNodesCollapsed = useStableCallback(onSetAllNodesCollapsed);
  const stableSetWireStyle = useStableCallback(wireStylePref.setWireStyle);
  const stableSetGridOn = useStableCallback(overlays.setGridOn);
  const stableSetMiniOn = useStableCallback(overlays.setMiniOn);
  const stableUndo = useStableCallback(graph.undo);
  const stableRedo = useStableCallback(graph.redo);
  const stableRemoveSelectedNodes = useStableCallback(onRemoveSelectedNodes);
  const stableDuplicateSelectedNodes = useStableCallback(onDuplicateSelectedNodes);
  const stableAutoArrange = useStableCallback(onAutoArrange);
  const stableZoomIn = useStableCallback(onZoomIn);
  const stableZoomOut = useStableCallback(onZoomOut);
  const stableFitView = useStableCallback(onFitView);
  const stableRecenter = useStableCallback(onRecenter);
  const stableClearNodeStatuses = useStableCallback(onClearStatuses);

  // Gate-waiting state used by both the modal (further down) and the
  // in-canvas affordance on the gate node's branch pills. Kept here
  // (above {@code editorContext}) because the context exposes a
  // per-node accessor and a "reopen the modal" handler so leaf nodes
  // can recover from a stranded "Decide later" state without forcing
  // a page refresh.
  const [dismissedGateKey, setDismissedGateKey] = useState<string | null>(null);
  const [gatePrefillDecision, setGatePrefillDecision] =
    useState<GateDecisionValue | null>(null);
  const activeGate = session.gateWaiting;
  const activeGateKey = activeGate
    ? `${activeGate.nodeId}:${activeGate.iteration}`
    : null;
  // Reset the "Decide later" suppression whenever the waiting gate's
  // identity changes — either the run cleared it (decision made
  // elsewhere, gate expired, run failed) or a new gate took over.
  // Without this, a later gate with the same {nodeId}:{iteration} key
  // (e.g. a re-run of the same draft) would be silently suppressed by
  // a stale dismissal and the user would have to click the in-canvas
  // branch pill to recover. {@link requestGateDecision} also clears
  // these on demand for the same-gate reopen path; this effect covers
  // the cross-gate transitions that handler never sees.
  useEffect(() => {
    setDismissedGateKey(null);
    setGatePrefillDecision(null);
  }, [activeGateKey]);
  const showGateDialog =
    activeGate != null && activeGateKey !== dismissedGateKey;
  const onDismissGateDialog = useCallback(() => {
    if (activeGateKey) setDismissedGateKey(activeGateKey);
    setGatePrefillDecision(null);
  }, [activeGateKey]);
  const gateReviewArtifacts = useMemo(
    () =>
      activeGate
        ? (session.liveStateForNode(activeGate.nodeId)?.artifacts ?? []).filter(
            (a) => a.iteration === activeGate.iteration,
          )
        : [],
    [activeGate, session],
  );
  const getGateWaitingForNode = useCallback(
    (nodeId: string) =>
      activeGate != null && activeGate.nodeId === nodeId
        ? { iteration: activeGate.iteration }
        : null,
    [activeGate],
  );
  const requestGateDecision = useCallback(
    (nodeId: string, decision?: GateDecisionValue) => {
      // Only reopen for the currently-waiting gate; ignore stale clicks
      // on inactive branches (e.g. on a completed gate from a prior
      // iteration). Defensive — BranchRow gates its own clickability,
      // but the context method shouldn't trust the caller blindly.
      if (!activeGate || activeGate.nodeId !== nodeId) return;
      setDismissedGateKey(null);
      setGatePrefillDecision(decision ?? null);
    },
    [activeGate],
  );
  const stableRequestGateDecision = useStableCallback(requestGateDecision);

  // Runner-claim consent prompt — mirrors the gate "Decide later" flow so a
  // dismissed prompt can be recovered without a refresh.
  const [isAcceptingRunnerClaim, setIsAcceptingRunnerClaim] = useState(false);
  const [dismissedRunnerWaitKey, setDismissedRunnerWaitKey] =
    useState<string | null>(null);
  const activeRunnerWait = session.runnerWaiting;
  const activeRunnerWaitKey = activeRunnerWait
    ? `${activeRunnerWait.nodeId}:${activeRunnerWait.iteration}`
    : null;
  useEffect(() => {
    setDismissedRunnerWaitKey(null);
  }, [activeRunnerWaitKey]);
  const runnerWaitPending =
    activeRunnerWait != null && activeRunnerWaitKey !== dismissedRunnerWaitKey;
  // The waiting node's required apps (a desktop agent needs its bridge app,
  // e.g. blender_desktop → "blender"; headless agents need none). Used to gate
  // the prompt so we never ask a user to allow a run their runner can't satisfy.
  const { data: nodeDefinitions } = useNodeDefinitions();
  const requiredAppsForWait = useMemo<string[]>(() => {
    if (!activeRunnerWait) return [];
    return requiredAppsForSkills(
      nodeDefinitions?.find((d) => d.id === 'task')?.agentRequiredApps,
      nodesById.get(activeRunnerWait.nodeId)?.data?.skillIds,
    );
  }, [activeRunnerWait, nodesById, nodeDefinitions]);
  // Only prompt an assignee whose runner is idle, heartbeating right now, AND
  // advertises the node's required apps — a "waiting_for_runner" node also parks
  // when nobody's runner is connected (or when the online runner lacks the app,
  // e.g. no Blender bridge), and a killed runner lingers as READY until a slow
  // server sweep. Matching the allocator's own filter here stops an endless
  // accept→can't-claim→re-park→re-prompt loop. Server-side accept still enforces
  // the assignee/consent checks.
  const { data: myLocalRunners } = useMyLocalRunners(runnerWaitPending);
  const hasReadyRunner = hasClaimableRunner(myLocalRunners, requiredAppsForWait);
  // Fail closed until node definitions load: while pending, requiredAppsForWait
  // falls back to [] and hasClaimableRunner would treat that as "any live
  // runner", surfacing the prompt before the node's app requirements are known
  // and re-opening the accept→can't-claim→re-prompt loop. Wait for the catalog.
  const showRunnerClaimDialog =
    runnerWaitPending && nodeDefinitions != null && hasReadyRunner;
  const onDismissRunnerClaimDialog = useCallback(() => {
    if (activeRunnerWaitKey) setDismissedRunnerWaitKey(activeRunnerWaitKey);
  }, [activeRunnerWaitKey]);
  const onAcceptRunnerClaim = useCallback(async () => {
    if (isAcceptingRunnerClaim) return;
    setIsAcceptingRunnerClaim(true);
    try {
      await session.acceptRunnerClaim();
    } finally {
      setIsAcceptingRunnerClaim(false);
    }
  }, [isAcceptingRunnerClaim, session]);

  const manualPrompt = useManualCompletionPrompt(session);

  const editorContext = useMemo<EditorContextValue>(
    () => ({
      updateNodeData: stableUpdateNodeData,
      removeNode: stableRemoveNode,
      duplicateNode: stableDuplicateNode,
      readOnly: effectiveReadOnly,
      runStateForNode,
      getRunGate,
      errorsForNode,
      publishHintsForNode,
      isNodeGenerating,
      isRunActive: runInFlight,
      startNodeRun: stableStartNodeRun,
      cancelNodeRun: stableCancelNodeRun,
      getGateWaitingForNode,
      requestGateDecision: stableRequestGateDecision,
      refreshArtifactUrl: stableRefreshArtifactUrl,
      runHistoryOpen,
      setRunHistoryOpen: stableSetRunHistoryOpen,
      isNodeCollapsed: nodeCollapse.isCollapsed,
      toggleNodeCollapsed: stableToggleNodeCollapsed,
      setAllNodesCollapsed: stableSetAllNodesCollapsed,
      wireStyle: wireStylePref.wireStyle,
      setWireStyle: stableSetWireStyle,
      gridOn: overlays.gridOn,
      setGridOn: stableSetGridOn,
      miniOn: overlays.miniOn,
      setMiniOn: stableSetMiniOn,
      canUndo: graph.canUndo,
      canRedo: graph.canRedo,
      undo: stableUndo,
      redo: stableRedo,
      selectedNodeIds,
      selectedEdgeIds,
      connectionFocus,
      removeSelectedNodes: stableRemoveSelectedNodes,
      duplicateSelectedNodes: stableDuplicateSelectedNodes,
      autoArrange: stableAutoArrange,
      zoomIn: stableZoomIn,
      zoomOut: stableZoomOut,
      fitView: stableFitView,
      recenter: stableRecenter,
      hasNodeRunStates,
      clearNodeStatuses: stableClearNodeStatuses,
    }),
    // Deps are the value fields plus the render-time derivations
    // (errorsForNode / publishHintsForNode / runStateForNode /
    // getRunGate / isNodeCollapsed). The trampolined event handlers are
    // intentionally absent — they are stable for the lifetime of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      effectiveReadOnly,
      runStateForNode,
      getRunGate,
      errorsForNode,
      publishHintsForNode,
      isNodeGenerating,
      runInFlight,
      getGateWaitingForNode,
      runHistoryOpen,
      nodeCollapse.isCollapsed,
      wireStylePref.wireStyle,
      overlays.gridOn,
      overlays.miniOn,
      graph.canUndo,
      graph.canRedo,
      selectedNodeIds,
      selectedEdgeIds,
      connectionFocus,
      hasNodeRunStates,
    ],
  );

  const onBackToList = useCallback(() => {
    navigate('/processes');
  }, [navigate]);

  // ── Run + publish UI state ──────────────────────────────────────────────
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [isRunDialogOpen, setIsRunDialogOpen] = useState(false);
  const [isCancellingRun, setIsCancellingRun] = useState(false);
  const [isDecidingGate, setIsDecidingGate] = useState(false);
  const { isPublishing, publish: onPublish } = useProcessPublish({
    processId,
    detail: detailQuery.data,
    autoSave,
    queryClient,
    showSuccess,
    showError,
  });

  // ── Header kebab dialogs + actions ──────────────────────────────────────
  // Both the inline rename (breadcrumb) and the kebab's Rename dialog
  // route through {@link useProcessRename}'s `onCommitRename`.
  const { onCommitRename } = useProcessRename({
    processId,
    detail: detailQuery.data,
    autoSave,
    autoSaveEnabled,
    queryClient,
    showSuccess,
    showError,
  });

  const {
    openDialog,
    closeDialog,
    openDialogFor,
    onRenameSubmit,
    onDuplicateSubmit,
    onSendCopySubmit,
    onDeleteConfirm,
  } = useProcessSidePanelDialogs({
    processId,
    detailName: detailQuery.data?.name,
    processActions,
    navigate,
    showSuccess,
    showError,
    onCommitRename,
  });

  const onCloseTestRunDialog = useCallback(() => {
    setIsTestDialogOpen(false);
    session.clearInputError();
  }, [session]);

  const onStartTestRun = useCallback(
    async (values: Readonly<Record<string, RunConfigFormValue>>) => {
      const flushed = await flushBeforeStart();
      if (!flushed.ok) return;
      await session.startFullRun({
        values,
        isTest: true,
        expectedUpdatedAt: flushed.expectedUpdatedAt,
      });
      trackFunnelEvent('process_test_run_started', { processId });
      setIsTestDialogOpen(false);
    },
    [flushBeforeStart, session, processId],
  );

  const onCloseRunDialog = useCallback(() => {
    setIsRunDialogOpen(false);
    session.clearInputError();
  }, [session]);

  const onStartRun = useCallback(
    async (values: Readonly<Record<string, RunConfigFormValue>>) => {
      const flushed = await flushBeforeStart();
      if (!flushed.ok) return;
      await session.startFullRun({
        values,
        isTest: false,
        expectedUpdatedAt: flushed.expectedUpdatedAt,
      });
      setIsRunDialogOpen(false);
    },
    [flushBeforeStart, session],
  );

  useAutoOpenRunDialog({
    searchParams,
    setSearchParams,
    isPublished,
    hasInputNodes,
    onStartRun,
    setIsRunDialogOpen,
  });

  useAutoArrangeOnNavigation({
    searchParams,
    setSearchParams,
    rfInstance,
    nodes: graph.nodes,
    edges: graph.edges,
    applyLayout,
  });

  const onCancelActiveRun = useCallback(async () => {
    if (isCancellingRun) return;
    setIsCancellingRun(true);
    try {
      await session.cancelActive();
    } finally {
      setIsCancellingRun(false);
    }
  }, [isCancellingRun, session]);

  const onDecideGate = useCallback(
    async (decision: GateDecisionValue, comment?: string) => {
      if (isDecidingGate) return;
      setIsDecidingGate(true);
      try {
        await session.decideGate(decision, comment);
      } finally {
        setIsDecidingGate(false);
      }
    },
    [isDecidingGate, session],
  );


  const dockCollapsed = useDockCollapsedSync();

  const assistantPanel = useAssistantPanelState(processId, isPublished);
  const graphSync = useProcessGraphSync({
    processId,
    queryClient,
    isDirty: graph.isDirty,
    setGraph: graph.setGraph,
    adoptExternalSave: autoSave.adoptExternalSave,
    cancelExternalSaveAdoption: autoSave.cancelExternalSaveAdoption,
  });

  // A streaming run owns the detail cache -- {@link useProcessRunSession}
  // invalidates it repeatedly -- and while the drift modal is up only the
  // user's answer to it may move the baseline. A published process has
  // nothing to converge on.
  const syncSuspended =
    isPublished ||
    isRunActive ||
    Boolean(detailQuery.data?.hasActiveTestRun) ||
    showDriftBlocker;

  // Read the suspension and the reporter through refs so the callback below
  // keeps a stable identity: the assistant panel fires it from an effect
  // keyed on it, and a new function every render would re-fire that effect.
  const syncSuspendedRef = useRef(syncSuspended);
  const showErrorRef = useRef(showError);
  useEffect(() => {
    syncSuspendedRef.current = syncSuspended;
    showErrorRef.current = showError;
  });

  const { syncNow: syncGraph } = graphSync;
  const syncGraphFromServer = useCallback(async () => {
    if (syncSuspendedRef.current) return;
    if ((await syncGraph()) === 'failed') {
      showErrorRef.current(
        'Could not load the latest changes. Refresh to load them.',
      );
    }
  }, [syncGraph]);

  useEffect(() => {
    syncGraphFromServerRef.current = () => void syncGraphFromServer();
  }, [syncGraphFromServer]);

  // AIB-8117: the turn boundary is a single signal on a single connection,
  // and a tab switch mid-turn routes the end of the turn through an abort
  // plus reconnect. Reconciling on return means a missed edge costs one
  // conditional GET instead of a manual page reload.
  const visibilitySyncHandleRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (visibilitySyncHandleRef.current) {
        clearTimeout(visibilitySyncHandleRef.current);
      }
      visibilitySyncHandleRef.current = setTimeout(() => {
        visibilitySyncHandleRef.current = null;
        void syncGraphFromServer();
      }, VISIBILITY_SYNC_DEBOUNCE_MS);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (visibilitySyncHandleRef.current) {
        clearTimeout(visibilitySyncHandleRef.current);
        visibilitySyncHandleRef.current = null;
      }
    };
  }, [syncGraphFromServer]);

  // Drain whatever was owed while a run held the cache or the drift modal
  // held the baseline. The hook drains the dirty case itself; this covers
  // the suspensions only the page knows about.
  const wasSyncSuspendedRef = useRef(syncSuspended);
  useEffect(() => {
    const cleared = wasSyncSuspendedRef.current && !syncSuspended;
    wasSyncSuspendedRef.current = syncSuspended;
    if (cleared) void syncGraphFromServer();
  }, [syncSuspended, syncGraphFromServer]);

  // The topbar's Refresh action. A real 409 has to go through the drift
  // handler so autosave re-baselines; "Changes available" has no 409 behind
  // it, so it takes the server's version directly and must NOT arm drift
  // recovery -- arming it outside a 409 leaks forward and silently dismisses
  // the next genuine drift modal.
  //
  // The waiting-changes path only exists when the canvas is dirty, and the
  // reseed resets undo history, so those edits are gone for good. One
  // misclick is not consent for that; confirm first.
  const { discardLocalEditsAndSync } = graphSync;
  const [confirmDiscardForSync, setConfirmDiscardForSync] = useState(false);
  const onTopBarRefresh = useCallback(() => {
    if (autoSave.status === 'drift') {
      onRefreshAfterDrift();
      return;
    }
    setConfirmDiscardForSync(true);
  }, [autoSave.status, onRefreshAfterDrift]);
  const onConfirmDiscardForSync = useCallback(() => {
    setConfirmDiscardForSync(false);
    void discardLocalEditsAndSync();
  }, [discardLocalEditsAndSync]);

  const onPaletteDrop = useCallback(
    (node: ProcessNode) => {
      graph.addNode(node);
      trackFunnelEvent('process_node_added', {
        processId,
        nodeKind: node.type,
      });
    },
    [graph, processId],
  );

  const onSelectionChange = useCallback(
    ({ nodes, edges }: OnSelectionChangeParams) => {
      // RF fires this on every drag tick with a fresh array. Returning
      // {@code curr} when the id set is unchanged lets React's Object.is
      // bail-out skip the setState entirely — without it the new array
      // reference would invalidate {@link editorContext} and re-render
      // every {@link ProcessNode} via context (memo doesn't help there).
      const firstId = nodes[0]?.id ?? null;
      setSelectedNodeId((curr) => (curr === firstId ? curr : firstId));
      setSelectedNodeIds((curr) => stableIdList(curr, nodes));
      setSelectedEdgeIds((curr) => stableIdList(curr, edges));
    },
    [],
  );

  // Conditional callbacks pre-computed via useMemo so their conditional
  // construction is scored as separate functions for cognitive-complexity
  // purposes — keeping the JSX render tree below a flat tree of prop
  // assignments rather than a nest of inline ternaries.
  const publishCallback = useMemo(() => {
    if (
      isPublished ||
      isRunActive ||
      errorCount > 0 ||
      publishBlockerCount > 0 ||
      isPublishing
    ) {
      return undefined;
    }
    return () => {
      onPublish().catch(() => undefined);
    };
  }, [
    isPublished,
    isRunActive,
    errorCount,
    publishBlockerCount,
    isPublishing,
    onPublish,
  ]);
  const sidePanelOnRename = useMemo(() => {
    if (!detailQuery.data?.isOwner || isRunActive) return undefined;
    return openDialogFor('rename');
  }, [detailQuery.data?.isOwner, isRunActive, openDialogFor]);
  const sidePanelDeleteDisabled = useMemo(() => {
    const owner = detailQuery.data?.isOwner ?? false;
    return !owner;
  }, [detailQuery.data?.isOwner]);
  const sidePanelDeleteTooltip = useMemo(
    () =>
      detailQuery.data
        ? formatDeleteTooltip(detailQuery.data.isOwner)
        : undefined,
    [detailQuery.data],
  );
  // Existing-name list for the duplicate dialogs. Memoised together so
  // each consumer reads the same array reference — small win for
  // shallow-equality dialog memoisation, and one fewer .map() in render.
  const existingProcessNames = useMemo(
    () => processSummaries.map((p) => p.name),
    [processSummaries],
  );
  const onCloseRunHistory = useCallback(
    () => setRunHistoryOpen(false),
    [],
  );
  const onNavigateToProcess = useCallback(
    (id: string) => navigate(`/processes/${id}`),
    [navigate],
  );
  const onNewProcess = useCallback(
    () => navigate('/processes'),
    [navigate],
  );

  const loadGuard = renderEditorLoadGuard(processId, detailQuery);
  if (loadGuard) return loadGuard;
  // Both narrow to non-nullable past this point: the guard above
  // returns the empty-state JSX whenever either is missing, so reaching
  // here means we have a route id AND a loaded detail payload. The
  // assertions appease the type system without re-checking at runtime.
  const detail = detailQuery.data!;
  const safeProcessId = processId!;

  const pageRootDataAttrs = buildPageRootDataAttrs({
    selectedNodeId,
    errorCount,
    publishBlockerCount,
    validationValid: validation.valid,
    isChecking,
    isPublished,
    isDirty: graph.isDirty,
  });

  return (
    <PageRoot {...pageRootDataAttrs}>
      <ProcessEditorTopBar
        processName={detail.name}
        saveStatus={isPublished ? undefined : autoSave.status}
        saveLastSavedAt={autoSave.lastSavedAt}
        saveErrorMessage={autoSave.errorMessage}
        saveValidationErrorCount={errorCount}
        onSaveRefresh={onTopBarRefresh}
        isSyncing={graphSync.isSyncing}
        changesWaiting={graphSync.deferredChangesWaiting}
        onBackToList={onBackToList}
      />
      <DriftBlockerModal
        open={showDriftBlocker}
        onRefresh={onRefreshAfterDrift}
        onForceSave={onForceSaveOverDrift}
        isForceSaving={autoSave.isForceSaving}
      />
      {isRunActive && session.streamDisconnected ? (
        <SseConnectionLostBanner
          isReconnecting={session.isReconnecting}
          onReload={() => globalThis.location.reload()}
        />
      ) : null}
      <CanvasArea
        $dockCollapsed={dockCollapsed}
        $assistantWidth={assistantPanel.width}
      >
        <EditorContextProvider value={editorContext}>
          <GraphCanvas
            nodes={graph.nodes}
            edges={graph.edges}
            onNodesChange={graph.onNodesChange}
            onEdgesChange={graph.onEdgesChange}
            onConnect={graph.connect}
            onPaletteDrop={onPaletteDrop}
            onSelectionChange={onSelectionChange}
            readOnly={effectiveReadOnly}
            nodesDraggable={canDragNodes}
            onCursorWorldPosRef={onCursorWorldPosRef}
            onInit={onCanvasInit}
            overlays={
              <EditorOverlays
                isRunActive={isRunActive}
                runHistoryOpen={runHistoryOpen}
                isPublished={isPublished}
                processId={processId!}
                processName={detail.name}
                // Toolbar's "Duplicate & edit" opens the confirmation
                // dialog (auto-named, no input) -- the side-panel kebab
                // below opens the input-based dialog where the user
                // can pick a name. Two dialogs, two flows.
                onDuplicateProcess={openDialogFor('duplicate-confirm')}
                // No backend Unpublish endpoint yet -- render the
                // button disabled with a tooltip until the API
                // surface lands.
                onUnpublishProcess={undefined}
                onCloseRunHistory={onCloseRunHistory}
                validation={validation}
                runFailures={runFailures}
                nodeCount={stableNodes.length}
                connectionCount={stableEdges.length}
                isValidationChecking={isChecking}
                onFocusNode={onFocusNode}
                onFocusEdge={onFocusEdge}
                runs={runs}
                isRunsLoading={isRunsLoading}
              />
            }
          />
        </EditorContextProvider>
      </CanvasArea>
      <ProcessAssistantPanel
        processId={safeProcessId}
        state={assistantPanel}
        dockCollapsed={dockCollapsed}
        onProcessEdited={syncGraphFromServer}
        onBusyChange={setAssistantBusy}
        onBeforeSend={flushBeforeAssistantTurn}
      />
      <ProcessSidePanel
        processName={detail.name}
        processId={safeProcessId}
        isPublished={isPublished}
        nodes={stableNodes}
        edges={stableEdges}
        nodeSelected={!!selectedNodeId}
        selectedNodeTranscript={selectedNode.transcript}
        selectedNodeError={selectedNode.failure?.message}
        selectedNodeErrorDetail={selectedNode.failure?.detail}
        selectedNodeIsLive={selectedNode.liveState?.status === 'running'}
        ownerName={ownerName}
        processes={processSummaries}
        onPublish={publishCallback}
        isPublishing={isPublishing}
        runDisabled={errorCount > 0}
        onRun={() => {
          if (hasInputNodes) {
            setIsRunDialogOpen(true);
          } else {
            onStartRun({}).catch(() => undefined);
          }
        }}
        isRunning={isPublished && isRunActive}
        hasInput={hasInputNodes}
        onNavigateToProcess={onNavigateToProcess}
        onNewProcess={onNewProcess}
        onShowDetails={openDialogFor('details')}
        onRenameProcess={sidePanelOnRename}
        onDuplicateProcess={openDialogFor('duplicate')}
        onSendCopy={openDialogFor('send-copy')}
        onDeleteProcess={openDialogFor('delete')}
        deleteDisabled={sidePanelDeleteDisabled}
        deleteTooltip={sidePanelDeleteTooltip}
        onTestRun={() => {
          if (hasInputNodes) {
            setIsTestDialogOpen(true);
          } else {
            onStartTestRun({}).catch(() => undefined);
          }
        }}
        onStopTestRun={() => {
          onCancelActiveRun().catch(() => undefined);
        }}
        onStopRun={() => {
          onCancelActiveRun().catch(() => undefined);
        }}
        isCancelling={isCancellingRun}
        testRunPhase={
          liveExecData?.phase ?? lastRunLog?.phase ?? 'idle'
        }
        testRunTotal={
          liveExecData?.total ?? lastRunLog?.total ?? 0
        }
        testRunOrder={
          liveExecData?.order ?? lastRunLog?.order ?? []
        }
        testRunCurrentId={
          liveExecData?.currentId ?? lastRunLog?.currentId ?? null
        }
        testRunStartedAt={
          liveExecData?.startedAt ?? lastRunLog?.startedAt ?? null
        }
        testRunFinishedAt={
          liveExecData?.finishedAt ?? lastRunLog?.finishedAt ?? null
        }
        runNumber={liveExecData?.runNumber ?? lastRunLog?.runNumber}
        isTest={liveExecData?.isTest ?? lastRunLog?.isTest}
        runOutcome={
          liveExecData?.runOutcome ?? lastRunLog?.runOutcome
        }
        runnerLabel={runnerLabel}
        runHistoryStats={runHistoryStats}
      />
      <ProcessDetailsDialog
        open={openDialog === 'details'}
        onClose={closeDialog}
        detail={detail}
      />
      <ProcessRenameDialog
        open={openDialog === 'rename'}
        onClose={closeDialog}
        currentName={detail.name}
        onSubmit={onRenameSubmit}
        isRenaming={autoSave.status === 'saving'}
      />
      <ProcessDuplicateDialog
        open={openDialog === 'duplicate'}
        onClose={closeDialog}
        currentName={detail.name}
        existingNames={existingProcessNames}
        onSubmit={onDuplicateSubmit}
        isDuplicating={processActions.isDuplicating}
      />
      <ProcessDuplicateConfirmDialog
        open={openDialog === 'duplicate-confirm'}
        onClose={closeDialog}
        currentName={detail.name}
        existingNames={existingProcessNames}
        onSubmit={onDuplicateSubmit}
        isDuplicating={processActions.isDuplicating}
      />
      <ProcessSendCopyDialog
        open={openDialog === 'send-copy'}
        onClose={closeDialog}
        processName={detail.name}
        onSubmit={onSendCopySubmit}
      />
      <ConfirmDialog
        open={confirmDiscardForSync}
        onClose={() => setConfirmDiscardForSync(false)}
        onConfirm={onConfirmDiscardForSync}
        title="Discard your edits?"
        description={
          <>
            This process was changed elsewhere. Loading those changes replaces
            the canvas, so your unsaved edits will be lost and cannot be undone.
            <br />
            <br />
            Save them first if you want to keep them.
          </>
        }
        tone="danger"
        confirmLabel="Discard & refresh"
        icon={null}
        dataTestId="process-editor__discard-for-sync-confirm"
      />
      <ConfirmDialog
        open={openDialog === 'delete'}
        onClose={closeDialog}
        onConfirm={onDeleteConfirm}
        title="Delete Process"
        description={
          <>
            <strong>{detail.name}</strong> will be permanently deleted
            and all its configurations will be lost.
            {detail.status === 'published' && (
              <>
                <br />
                <br />
                {PUBLISHED_PROCESS_DELETE_NOTE}
              </>
            )}
            <br />
            <br />
            Are you sure you want to proceed?
          </>
        }
        tone="danger"
        confirmLabel="Confirm"
        icon={null}
        loading={processActions.isRemoving}
        dataTestId="process-delete-dialog"
      />
      <TestRunDialog
        open={isTestDialogOpen}
        onClose={onCloseTestRunDialog}
        nodes={graph.nodes}
        isStarting={session.startInFlight}
        inputError={session.inputError}
        onStart={onStartTestRun}
        onClearError={session.clearInputError}
      />
      <RunDialog
        open={isRunDialogOpen}
        onClose={onCloseRunDialog}
        nodes={graph.nodes}
        isStarting={session.startInFlight}
        inputError={session.inputError}
        onStart={onStartRun}
        onClearError={session.clearInputError}
      />
      <GateDecisionDialog
        gate={showGateDialog ? activeGate : null}
        nodes={graph.nodes}
        isDeciding={isDecidingGate}
        onDecide={onDecideGate}
        onClose={onDismissGateDialog}
        prefillDecision={showGateDialog ? gatePrefillDecision : null}
        reviewArtifacts={gateReviewArtifacts}
        refreshArtifactUrl={stableRefreshArtifactUrl}
      />
      <RunnerClaimDialog
        runnerWait={showRunnerClaimDialog ? activeRunnerWait : null}
        nodes={graph.nodes}
        isAccepting={isAcceptingRunnerClaim}
        onAccept={onAcceptRunnerClaim}
        onClose={onDismissRunnerClaimDialog}
      />
      <ManualCompletionDialog
        manualWait={manualPrompt.manualWait}
        nodes={graph.nodes}
        inputArtifacts={manualPrompt.inputArtifacts}
        isCompleting={manualPrompt.isCompleting}
        onComplete={manualPrompt.onComplete}
        onClose={manualPrompt.onDismiss}
        refreshArtifactUrl={stableRefreshArtifactUrl}
      />
      <InputValueDialog
        open={pendingInputNode != null}
        node={pendingInputNode}
        inputError={session.inputError}
        isStarting={session.startInFlight}
        onClose={onCloseInputDialog}
        onClearError={session.clearInputError}
        onSubmit={onSubmitInputValue}
      />
    </PageRoot>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
 * Route wrapper — decides between the full editor and the read-only
 * historical run view. When a `runId` param is present in the URL
 * (`/processes/:processId/runs/:runId`) we render a lightweight shell
 * that reuses the same PageRoot + ProcessEditorTopBar chrome but swaps
 * the canvas area and right dock for RunDetailView. This avoids
 * instantiating all the heavy editor hooks (auto-save, graph, session,
 * validation, etc.) for a purely read-only snapshot.
 * ═══════════════════════════════════════════════════════════════════════ */
const RunDetailShell: React.FC<{
  processId: string;
  runId: string;
}> = ({ processId, runId }) => {
  const navigate = useNavigate();
  const detailQuery = useProcessDetail(processId);

  const onBackToList = useCallback(() => {
    navigate(`/processes/${processId}`);
  }, [navigate, processId]);

  const loadGuard = renderEditorLoadGuard(processId, detailQuery);
  if (loadGuard) return loadGuard;

  const detail = detailQuery.data!;

  return (
    <PageRoot data-testid="process-editor">
      <ProcessEditorTopBar
        processName={detail.name}
        onBackToList={onBackToList}
      />
      <RunDetailView processId={processId} runId={runId} />
    </PageRoot>
  );
};

const ProcessEditorPage: React.FC = () => {
  const { processId, runId } = useParams<{
    processId: string;
    runId?: string;
  }>();

  if (runId && processId) {
    return <RunDetailShell processId={processId} runId={runId} />;
  }

  return <ProcessEditorInner />;
};

export default ProcessEditorPage;
